// @flow
import './RadioInput.css';

import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import InputCreator, { $defaultInputProps } from '../../containers/ProductForm/InputCreator';
import Show from '../../functional/Show';
import { getDangerousHtml } from '../../helpers/components';
import { equals, optionFind } from '../../helpers/functions';
import { optionGet } from '../../helpers/objects';
import { wrapOptionId } from '../../helpers/strings';
import { attributesSelector } from '../../store/modules/attributes';
import { hoveredOptionSelector } from '../../store/modules/configuration';
import { $SwatchImageAttributeType, $SwatchTextAttributeType } from '../../types/attributes';
import BookSizeSwatch from './BookSizeSwatch';
import CircularImageSwatch from './CircularImageSwatch';
import FoilColorSwatch from './FoilColorSwatch';
import { any } from '../../helpers/conditionals';
import { isInArray } from '../../helpers/arrays';
// Attribute Swatch Types
const ALBUM_SIZE = 'album_size';
const BOOK_SIZE = 'book_size';
const HARDCOVER_BOOK_SIZE = 'hardcover_book_size';
const SOFTCOVER_BOOK_SIZE = 'softcover_book_size';
const COVER_MATERIAL = 'cover_material';
const FABRIC_COLOR = 'fabric_color';
const LINEN_ID = '5887';
const LEATHER_ID = '5886';
const LEATHER_COLOR = 'leather_color';
const COVER_COLOR = 'cover_color';
const FOIL_COLOR = 'foil_color';
const PAPER = 'paper';
const COMPLETE_YOUR_SET = 'complete_your_set';
const FRAME_BACKING = 'frame_backing';
const FRAME_FINISH = 'frame_finish';
const ENVELOPE = 'envelope';

// Book Equality Helper Functions
const isPaper = attrType => equals(PAPER)(attrType);
const isBoxSet = attrType => equals(COMPLETE_YOUR_SET)(attrType);
const isFoilColor = attrType => equals(FOIL_COLOR)(attrType);
const isFrameBacking = attrType => equals(FRAME_BACKING)(attrType);
const isBookSizeSwatch = attrType =>
  isInArray(attrType)([ALBUM_SIZE, HARDCOVER_BOOK_SIZE, BOOK_SIZE, SOFTCOVER_BOOK_SIZE]);
const isCircularImageSwatch = attrType =>
  isInArray(attrType)([FABRIC_COLOR, COVER_COLOR, FRAME_FINISH, ENVELOPE]);

// Checks for the React PDP Input Type Option (So we only render image swatch if this value resolves to `image_swatch`)
// const isImageSwatch = inputType => inputType === 'image_swatch';

const hasHoverDescription = attr =>
  any([isPaper(attr), isBookSizeSwatch(attr), isBoxSet(attr), isFrameBacking(attr)]);

const cssClassFromType = (type: 'swatch_visual_image' | 'swatch_text' | 'drop_down'): string =>
  (type === 'drop_down' ? 'swatch_text' : type);

const swatchContainerClassFromType = (attrType, checked = false) => {
  // (eg: PDPRedesign--book_size-Swatch-Container-Checked)
  let className = 'PDPRedesign--';

  if (isBookSizeSwatch(attrType)) {
    className += 'AlbumSize';
  } else if (isCircularImageSwatch(attrType)) {
    className += 'CircularImage';
  } else if (isFoilColor(attrType)) {
    className += 'FoilColor';
  } else {
    className += 'Text';
  }

  className += '-Swatch-Container';

  if (checked) {
    className += '-Checked';
  }

  return className;
};

const OptionLabelRenderer = attribute => mediaUrl => option => mouseActionProps => (attributes) => {
  const labelProps = {
    ...mouseActionProps,
    alt: option.label,
    tabIndex: '0',
    onMouseEnter: e =>
      mouseActionProps.onMouseEnter(option, e.target.parentNode.parentNode.offsetTop),
  };

  const swatchProps = {
    option,
    mediaUrl,
  };

  // PDPRedesign A/B Test -- This is where we can hook into the radio button logic before they get rendered

  // These are the most reliable 'attribute types' to determine the swatch type
  const attributeType = attribute.code || attribute.label;

  if (isBookSizeSwatch(attributeType)) {
    return <BookSizeSwatch labelProps={labelProps} swatchProps={swatchProps} />;
  } else if (isCircularImageSwatch(attributeType)) {
    return <CircularImageSwatch option={option} labelProps={labelProps} mediaUrl={mediaUrl} />;
  } else if (isFoilColor(attributeType)) {
    // Ensuring react_pdp_input_type is 'image_swatch" before we render an image swatch here
    // Otherwise we should render a regular text swatch and skip all of this

    /* This component is dependent on the fabric/paper color of the book,
       so we need to find the currently selected color */
    const coverMaterial = optionFind(x => x.default_title === COVER_MATERIAL, attributes)
      .getOrElseValue({
        options: [],
      });
    const fabricAttr = optionFind(x => x.code === FABRIC_COLOR, attributes)
      .alt(optionFind(x => x.code === COVER_COLOR, attributes))
      .getOrElseValue({
        options: [],
      });
    const leatherAttr = optionFind(x => x.code === LEATHER_COLOR, attributes)
      .alt(optionFind(x => x.code === COVER_COLOR, attributes))
      .getOrElseValue({
        options: [],
      });
    if (coverMaterial.value === LINEN_ID) {
      return (
        <FoilColorSwatch labelProps={labelProps} option={option} mediaUrl={mediaUrl} attr={fabricAttr} />
      );
    } else if (coverMaterial.value === LEATHER_ID) {
      return (
        <FoilColorSwatch labelProps={labelProps} option={option} mediaUrl={mediaUrl} attr={leatherAttr} />
      );
    }
  }
  // END =========== PDP REDESIGN A/B TEST =============
  switch (attribute.type) {
    case 'swatch_visual_image': {
      return (
        <label {...labelProps} htmlFor={wrapOptionId(option.id)}>
          <img
            alt={option.label}
            src={`${mediaUrl}attribute/swatch/swatch_image/56x56${option.display_label}`}
          />
        </label>
      );
    }

    default: {
      return (
        <Fragment>
          <label
            className="PDPRedesign--ControlElement"
            {...labelProps}
            htmlFor={wrapOptionId(option.id)}
            dangerouslySetInnerHTML={getDangerousHtml(option.display_label)}
          />
          <label
            className="PDPRedesign--Text-Swatch"
            {...labelProps}
            htmlFor={wrapOptionId(option.id)}
            dangerouslySetInnerHTML={getDangerousHtml(option.display_label)}
          />
        </Fragment>
      );
    }
  }
};

type RadioInputProps = {
  defaultInputProps: $defaultInputProps,
  attribute: $SwatchImageAttributeType | $SwatchTextAttributeType,
  disabledOptions: Array<number>,
  mediaUrl: string,
  onMouseEnter: Function,
  onMouseLeave: Function,
  attributes: any
};

const RadioInput = InputCreator(
  ({
    defaultInputProps,
    attribute,
    disabledOptions,
    mediaUrl,
    onMouseEnter,
    onMouseLeave,
    attributes,
    hoveredOption,
  }: RadioInputProps) => {
    const mouseActionProps = {
      onMouseEnter,
      onMouseLeave,
    };

    // PDPRedesign -- Get the label attribute for any option that is being hovered
    // We'll use it to determine which description to render on mobile
    const hoveredAttrType = optionGet('label')(hoveredOption)
      .getOrElseValue('')
      .toLowerCase();

    // This identifies the current attribute that is generating a radio-group
    const currentAttr = attribute.code || attribute.label || attribute.title;
    const swatchClassName = checked => swatchContainerClassFromType(currentAttr, checked);
    const className = cssClassFromType(attribute.type);

    /*
      Since all radio-groups are created from this component, this awful conditional was the best way
      I could target the correct radio-group to display hover description. The upcoming Gatsby rewrite
      will solve this problem however I do acknowledge that this is the stinkiest of the stinky
    */
    const shouldDisplayDescription =
      (isPaper(currentAttr) && hoveredAttrType.includes('paper')) ||
      (isBookSizeSwatch(currentAttr) &&
        hoveredAttrType.includes('x') &&
        !hoveredAttrType.includes('box')) ||
      (isBoxSet(currentAttr) && hoveredAttrType.includes('box')) ||
      (isFrameBacking(currentAttr) &&
        (hoveredAttrType.includes('back') || hoveredAttrType.includes('point')));

    return (
      <Fragment>
        <div className={`pdp-radio-group ${currentAttr}`}>
          {attribute.options
          // @todo we should probably get a hold of these binary strings and properly make them bools
            .filter(opt => !(opt.visible_on_pdp && opt.visible_on_pdp === '0'))
            .map((opt) => {
              const checked = opt.id === attribute.value;

              return (
                <div
                  className={`pdp-radio__contain pdp-radio__contain--${className} ${swatchClassName(
                    checked,
                  )}`}
                >
                  <input
                    {...defaultInputProps}
                    className={`pdp-radio__input pdp-radio__input--${className} no-uniform`}
                    style={{ visibility: 'hidden' }}
                    id={wrapOptionId(opt.id)}
                    type="radio"
                    name={attribute.id}
                    data-test={attribute.default_title}
                    value={opt.id}
                    checked={checked}
                    disabled={disabledOptions.includes(opt.id) || opt.in_stock === '0'}
                  />
                  {OptionLabelRenderer(attribute)(mediaUrl)(opt)(mouseActionProps)(attributes)}
                </div>
              );
            })}
        </div>
        <Show when={hasHoverDescription(currentAttr)}>
          <div className="PDPRedesign--Mobile-PaperDescription">
            <Show when={shouldDisplayDescription}>{hoveredOption && hoveredOption.description}</Show>
          </div>
        </Show>
      </Fragment>
    );
  },
);

const mapStateToProps = state => ({
  attributes: attributesSelector(state),
  hoveredOption: hoveredOptionSelector(state),
});

export default connect(mapStateToProps)(RadioInput);



// WEBPACK FOOTER //
// ./src/components/RadioInput/index.js